import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useActions } from '../../providers/ActionsProvider'
import { Sections } from './Sections'
import { Seats } from './Seats'
import { useTooltips } from '../../providers/TooltipProvider/TooltipProvider'

const PAN_THRESHOLD_PX = 5
const THROTTLE_INTERVAL = 16
const MAX_WHEEL_DELTA = 100

function runViewportAnimation(from, to, durationMs, onFrame, onFinish, rafRef) {
  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current)
  }

  const start = performance.now()
  const tick = (now) => {
    const t = Math.min(1, (now - start) / durationMs)
    const k = t * (2 - t)
    onFrame({
      scale: from.scale + (to.scale - from.scale) * k,
      posX: from.posX + (to.posX - from.posX) * k,
      posY: from.posY + (to.posY - from.posY) * k,
    })

    if (t < 1) {
      rafRef.current = requestAnimationFrame(tick)
    } else {
      rafRef.current = null
      onFinish?.()
    }
  }

  rafRef.current = requestAnimationFrame(tick)
}

export default function SeatMap({
  data,
  isMapDisabled,
  selectRegularSeats,
  displayInventoryInfo,
  background,
  handleSectionSelect,
  deriveInventoryInfoFrom,
  getSectionState,
  getSeatState,
  getSeatTooltipDetails,
  onPurchaserClusterHover,
  useFrontendSeatSizing = false,
  seatScale,
  seatBorderRadius,
}) {
  const { showTooltip, hideTooltip } = useTooltips()
  const {
    stageRef,
    setScale,
    activeMapAction,
    calculateScalePercentage,
    originalScaleRef,
    SCALE_FACTOR,
    SCALE_LIMITS,
    setLoading,
  } = useActions()

  const originalWidth = background?.width // Original content width
  const originalHeight = background?.height // Original content height
  const aspectRatio = originalWidth / originalHeight

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [viewport, setViewport] = useState({ scale: 1, posX: 0, posY: 0 })
  const [isInteracting, setIsInteracting] = useState(false)

  const svgRef = useRef(null)
  const interactionTimeout = useRef(null)
  const wheelTimeout = useRef(null)
  const accumulatedDelta = useRef(0)
  const lastWheelTime = useRef(0)
  const isZooming = useRef(false)
  const zoomAnimRafRef = useRef(null)
  const pointerRef = useRef(null)
  const panRef = useRef({
    candidate: false,
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    origPosX: 0,
    origPosY: 0,
  })
  const viewportRef = useRef(viewport)
  const stateForStageRef = useRef({
    scale: viewport.scale,
    posX: viewport.posX,
    posY: viewport.posY,
    width,
    height,
  })

  viewportRef.current = viewport
  stateForStageRef.current = {
    scale: viewport.scale,
    posX: viewport.posX,
    posY: viewport.posY,
    width,
    height,
  }

  const resetInteractionState = useCallback(() => {
    if (interactionTimeout.current) clearTimeout(interactionTimeout.current)
    interactionTimeout.current = setTimeout(() => {
      setIsInteracting(false)
    }, 150)
  }, [])

  const pointerInSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    const rect = svg.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }, [])

  useEffect(() => {
    const resizeStage = () => {
      if (!originalWidth || !originalHeight || !aspectRatio) return
      const map = document.getElementById('map')
      if (!map) return

      const containerWidth = map.clientWidth
      const containerHeight = map.clientHeight
      const maxByWidth = containerWidth
      const maxByHeight = containerHeight * aspectRatio

      let newWidth
      let newHeight
      if (maxByWidth < maxByHeight) {
        newWidth = containerWidth
        newHeight = containerWidth / aspectRatio
      } else {
        newWidth = containerHeight * aspectRatio
        newHeight = containerHeight
      }

      const scaleX = newWidth / originalWidth
      const scaleY = newHeight / originalHeight
      const scale = Math.min(scaleX, scaleY) * 0.8
      originalScaleRef.current = scale

      setWidth(containerWidth)
      setHeight(containerHeight)
      setScale(scale)
      setViewport({ scale, posX: 0, posY: 0 })

      const adjustedOffsetX = (originalWidth - containerWidth / scale) / 2
      const adjustedOffsetY = (originalHeight - containerHeight / scale) / 2
      setOffset({ x: adjustedOffsetX, y: adjustedOffsetY })
    }

    resizeStage()
    window.addEventListener('resize', resizeStage)

    return () => window.removeEventListener('resize', resizeStage)
  }, [aspectRatio, originalHeight, originalScaleRef, originalWidth, setScale])

  useLayoutEffect(() => {
    stageRef.current = {
      scaleX: () => stateForStageRef.current.scale,
      x: () => stateForStageRef.current.posX,
      y: () => stateForStageRef.current.posY,
      width: () => stateForStageRef.current.width,
      height: () => stateForStageRef.current.height,
      setAttrs: (attrs) => {
        if (attrs.scaleX != null) {
          const nextScale = attrs.scaleX
          setViewport((v) => ({ ...v, scale: nextScale }))
          setScale(nextScale)
        }
        if (attrs.x != null || attrs.y != null) {
          setViewport((v) => ({
            ...v,
            posX: attrs.x ?? v.posX,
            posY: attrs.y ?? v.posY,
          }))
        }
      },
      batchDraw: () => {},
      getPointerPosition: () =>
        pointerRef.current ?? {
          x: stateForStageRef.current.width / 2,
          y: stateForStageRef.current.height / 2,
        },
      to: (config) => {
        const from = {
          scale: viewportRef.current.scale,
          posX: viewportRef.current.posX,
          posY: viewportRef.current.posY,
        }
        const to = {
          scale: config.scaleX,
          posX: config.x,
          posY: config.y,
        }
        const durationMs = (config.duration ?? 0.18) * 1000

        runViewportAnimation(
          from,
          to,
          durationMs,
          ({ scale, posX, posY }) => {
            setViewport({ scale, posX, posY })
            setScale(scale)
          },
          config.onFinish,
          zoomAnimRafRef
        )
      },
    }

    return () => {
      stageRef.current = null
    }
  }, [setScale, stageRef])

  useEffect(() => {
    if (!background?.url) {
      setLoading(false)
    }
  }, [background?.url, setLoading])

  const handleWheel = (e) => {
    const nativeWheelEvent = e?.nativeEvent ?? e
    if (nativeWheelEvent?.cancelable) {
      nativeWheelEvent.preventDefault()
    }
    setIsInteracting(true)
    pointerRef.current = pointerInSvg(e.clientX, e.clientY)

    accumulatedDelta.current += Math.min(
      Math.max(e.deltaY, -MAX_WHEEL_DELTA),
      MAX_WHEEL_DELTA
    )

    const now = performance.now()

    if (
      !isZooming.current &&
      now - lastWheelTime.current >= THROTTLE_INTERVAL
    ) {
      isZooming.current = true

      wheelTimeout.current = requestAnimationFrame(() => {
        const stage = stageRef.current
        if (!stage) return
        const oldScale = stage.scaleX()
        const pointer = stage.getPointerPosition()

        const direction = accumulatedDelta.current > 0 ? -1 : 1
        const zoomIntensity = Math.min(
          Math.abs(accumulatedDelta.current) / 35,
          1
        )
        const scaleBy = 1 + (SCALE_FACTOR - 1) * zoomIntensity

        const mousePointTo = {
          x: (pointer.x - stage.x()) / oldScale,
          y: (pointer.y - stage.y()) / oldScale,
        }

        let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy

        // Cap scale
        const wouldBeScale = calculateScalePercentage(newScale)
        if (
          wouldBeScale > SCALE_LIMITS.MAX ||
          wouldBeScale < SCALE_LIMITS.MIN
        ) {
          wheelTimeout.current = null
          isZooming.current = false
          accumulatedDelta.current = 0
          resetInteractionState()
          return false
        }

        const newPos = {
          x: pointer.x - mousePointTo.x * newScale,
          y: pointer.y - mousePointTo.y * newScale,
        }

        stage.setAttrs({
          scaleX: newScale,
          scaleY: newScale,
          x: newPos.x,
          y: newPos.y,
        })

        stage.batchDraw()
        setScale(newScale)

        wheelTimeout.current = null
        isZooming.current = false
        lastWheelTime.current = now
        accumulatedDelta.current = 0

        resetInteractionState()
        return true
      })
    }
  }

  const isInteractiveTarget = (target) =>
    Boolean(
      target?.closest?.(
        '[data-interactive-seat], [data-section-root], [data-row-selector]'
      )
    )

  const handlePointerDown = (e) => {
    if (e.button !== 0) return
    // Read-only maps (e.g. Sales Velocity heatmap): allow pan on seats/sections too.
    if (!isMapDisabled && isInteractiveTarget(e.target)) return

    panRef.current = {
      candidate: true,
      active: false,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origPosX: viewportRef.current.posX,
      origPosY: viewportRef.current.posY,
    }
  }

  const handlePointerMove = (e) => {
    const p = panRef.current
    if (p.pointerId !== e.pointerId) return

    if (p.candidate && !p.active) {
      const distance = Math.hypot(e.clientX - p.startX, e.clientY - p.startY)
      if (distance <= PAN_THRESHOLD_PX) return

      p.candidate = false
      p.active = true
      setIsInteracting(true)
      svgRef.current?.setPointerCapture?.(e.pointerId)
    }

    if (!p.active) return

    const dx = e.clientX - p.startX
    const dy = e.clientY - p.startY
    setViewport((v) => ({
      ...v,
      posX: p.origPosX + dx,
      posY: p.origPosY + dy,
    }))
  }

  const handlePointerUp = (e) => {
    const p = panRef.current
    if (p.pointerId !== e.pointerId) return

    if (p.active) {
      try {
        svgRef.current?.releasePointerCapture?.(e.pointerId)
      } catch {
        /* ignore */
      }
      resetInteractionState()
    }

    panRef.current = {
      candidate: false,
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      origPosX: 0,
      origPosY: 0,
    }
  }

  useEffect(() => {
    return () => {
      if (interactionTimeout.current) {
        clearTimeout(interactionTimeout.current)
      }
      if (zoomAnimRafRef.current) {
        cancelAnimationFrame(zoomAnimRafRef.current)
      }
      if (wheelTimeout.current) {
        cancelAnimationFrame(wheelTimeout.current)
      }
      accumulatedDelta.current = 0
      isZooming.current = false
      lastWheelTime.current = 0
    }
  }, [])

  const contentTransform = `translate(${viewport.posX}, ${viewport.posY}) scale(${viewport.scale}) translate(${-offset.x}, ${-offset.y})`

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        display: 'block',
        touchAction: 'none',
        cursor: isInteracting ? 'grabbing' : 'grab',
      }}
    >
      {width > 0 && height > 0 && (
        <g transform={contentTransform}>
          {background?.url ? (
            <image
              href={background.url}
              width={originalWidth}
              height={originalHeight}
              preserveAspectRatio="none"
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          ) : null}
          <Seats
            data={data}
            isDisabled={isMapDisabled}
            selectRegularSeats={selectRegularSeats}
            activeMapAction={activeMapAction}
            getSeatState={getSeatState}
            getSeatTooltipDetails={getSeatTooltipDetails}
            onPurchaserClusterHover={onPurchaserClusterHover}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            seatBorderRadius={seatBorderRadius}
          />
          <Sections
            data={data}
            isDisabled={isMapDisabled}
            displayInventoryInfo={displayInventoryInfo}
            activeMapAction={activeMapAction}
            getSectionState={getSectionState}
            handleSectionSelect={handleSectionSelect}
            deriveInventoryInfoFrom={deriveInventoryInfoFrom}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
          />
        </g>
      )}
    </svg>
  )
}
